import axios, { AxiosInstance, isAxiosError } from "axios";
import { queryClient } from "@/core/queryClient";
import { ACTIVE_SECTOR_HEADER, readActiveSectorId } from "../activeSector";

let _axiosSingletonInstance: AxiosInstance;

// Rotas que alimentam as queries invalidadas no 403: se uma delas der 403,
// invalidar as próprias queries dispararia a mesma request de novo, em laço.
const PERMISSION_SOURCE_PATHS = ["/user/me", "/user/account-accesses"];

const isPermissionSourceRequest = (url: string | undefined) => {
  const path = (url ?? "").split("?")[0].replace(/\/+$/, "");
  return PERMISSION_SOURCE_PATHS.some((source) => path.endsWith(source));
};

// SA-1352 §3.10: 403 é o sinal de que o perfil mudou por baixo da tela (um
// admin tirou uma página ou o acesso). Refaz as permissões do setor e os
// acessos para menu e gates se ajustarem sem recarregar. `cancelRefetch: false`
// agrupa uma rajada de 403 (página montando várias queries) num refetch só.
const refetchPermissions = () => {
  void queryClient.invalidateQueries(
    { queryKey: ["user", "me", "permissions"] },
    { cancelRefetch: false }
  );
  void queryClient.invalidateQueries(
    { queryKey: ["user", "account-accesses"] },
    { cancelRefetch: false }
  );
};

function api() {
  if (_axiosSingletonInstance) return _axiosSingletonInstance;
  _axiosSingletonInstance = axios.create({
    baseURL: `${import.meta.env.VITE_API_BASE_URL}/api`,
    headers: {
      // FIXME: Adaptar todas as requests CORE pro v5 e remover esse header
      "Strapi-Response-Format": "v4",
    },
  });

  // SA-1277: toda request declara o setor ativo. O backend usa isso para exigir
  // que chat/campanha acessado pertença à conta em que o usuário está operando
  // (antes bastava ter acesso ao setor DO RECURSO, então deep link de outra conta
  // respondia 200). Interceptor porque o setor troca em runtime e todas as
  // chamadas passam por esta instância.
  _axiosSingletonInstance.interceptors.request.use((config) => {
    const activeSectorId = readActiveSectorId();
    if (activeSectorId) {
      config.headers.set(ACTIVE_SECTOR_HEADER, activeSectorId);
    }
    return config;
  });

  _axiosSingletonInstance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (
        isAxiosError(error) &&
        error.response?.status === 403 &&
        !isPermissionSourceRequest(error.config?.url)
      ) {
        refetchPermissions();
      }
      return Promise.reject(error);
    }
  );

  return _axiosSingletonInstance;
}

export default api;
